/**
 * Feb-2026 (User-Wunsch 27.05.2026 — Refactoring P2) — Aus
 * `WorkshopOrdersPage.jsx` ausgelagerter Timesheet-Modal für Werkstatt-
 * Aufträge (Start/Stop pro Mitarbeiter, manuelle Einträge, Lohn-Rate).
 *
 * Keine Verhaltens-Änderung gegenüber der ursprünglichen Inline-Komponente.
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { X, Play, Square, Trash2, Check } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

export default function TimesheetModal({ order, employees = [], onClose, onUpdated }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    // Jul-2026 (User-Wunsch a) — AW-Satz aus den Einstellungen (1 AW = 6 min)
    const [rate, setRate] = useState("");
    useEffect(() => {
        api.get("/wms/montage-rate").then((r) => {
            const a = r.data;
            if (!a) return;
            const net = Number(a.sell_price_net || ((a.sell_price_gross || 0) / (1 + (a.vat_rate || 19) / 100)) || 0);
            if (net > 0) setRate(String(Math.round(net * 100) / 100));
        }).catch(() => {});
    }, []);
    // Phase 4c+ — „Stempeln für"-Dropdown (vorausgewählt: Mitarbeiter des Auftrags)
    const [stampEmployeeId, setStampEmployeeId] = useState(order.employee_id || "");

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/work-orders/${order.id}/timesheet`);
            setData(r.data);
        } catch (_e) {
            setData({ items: [], total_min: 0, active_entry: null });
        }
    }, [order.id]);

    useEffect(() => { load(); }, [load]);

    const startTimer = async (stopOther = false) => {
        setBusy(true);
        try {
            const payload = { ...(stampEmployeeId ? { employee_id: stampEmployeeId } : {}), ...(stopOther ? { stop_other: true } : {}) };
            await api.post(`/work-orders/${order.id}/timesheet/start`, payload);
            toast.success(stopOther ? "Anderer Timer beendet — Timer hier gestartet" : "Timer gestartet");
            load();
        } catch (e) {
            const det = e.response?.data?.detail;
            // Jul-2026 (User-Wunsch): läuft noch ein Timer auf einem anderen
            // Auftrag → erinnern und per Klick beenden + hier starten.
            if (det && det.code === "timer_running_elsewhere") {
                if (window.confirm(det.message)) { setBusy(false); return startTimer(true); }
            } else {
                toast.error(typeof det === "string" ? det : "Fehler");
            }
        }
        setBusy(false);
    };

    const stopTimer = async () => {
        setBusy(true);
        try {
            const payload = stampEmployeeId ? { employee_id: stampEmployeeId } : {};
            await api.post(`/work-orders/${order.id}/timesheet/stop`, payload);
            toast.success("Timer gestoppt");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
        setBusy(false);
    };

    const deleteEntry = async (eid) => {
        if (!window.confirm("Eintrag löschen?")) return;
        try {
            await api.delete(`/work-orders/${order.id}/timesheet/${eid}`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    };

    const convertToPosition = async () => {
        const r = Number(rate);
        if (!r || r <= 0) { toast.error("AW-Satz angeben"); return; }
        if (!window.confirm(`Erfasste Zeiten als AW-Lohnposition (${r} €/AW netto) übernehmen?`)) return;
        setBusy(true);
        try {
            const res = await api.post(`/work-orders/${order.id}/timesheet/to-position`, {
                aw_satz_eur: r, vat_rate: 19,
            });
            toast.success(`${res.data.total_min} min ≈ ${res.data.aw} AW als Lohnposition übernommen`);
            onUpdated();
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
        setBusy(false);
    };

    // Jul-2026 (User-Wunsch a) — automatische AW-Umrechnung der offenen Zeiten
    const openMin = (data?.items || [])
        .filter((e) => e.stopped_at && !e.invoiced)
        .reduce((s, e) => s + (Number(e.duration_min) || 0), 0);
    const openAw = Math.ceil(openMin / 6);

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="timesheet-modal">
            <div className="bg-white max-w-2xl w-full rounded-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
                    <div>
                        <div className="font-display font-black text-lg">Stempeluhr · {order.order_number}</div>
                        <div className="text-[11px] text-gray-500">Arbeitszeit pro Auftrag erfassen</div>
                    </div>
                    <button onClick={onClose} className="p-1 rounded-sm hover:bg-gray-100" data-testid="timesheet-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-5 space-y-4">
                    {employees.length > 0 && (
                        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3" data-testid="timesheet-stamp-for-wrap">
                            <label className="text-[10px] uppercase tracking-wider text-blue-900 font-bold">Stempeln im Namen von</label>
                            <select
                                value={stampEmployeeId}
                                onChange={(e) => setStampEmployeeId(e.target.value)}
                                disabled={!!data?.active_entry}
                                className="w-full mt-1 rounded-sm border border-blue-300 px-3 py-2 text-sm bg-white"
                                data-testid="timesheet-stamp-for"
                            >
                                <option value="">— Eigener Login (Standard) —</option>
                                {employees.map((emp) => (
                                    <option key={emp.id} value={emp.id}>
                                        {emp.name}
                                        {emp.employee_function ? ` · ${emp.employee_function}` : ""}
                                        {emp.mechanic_specialty ? ` (${emp.mechanic_specialty})` : ""}
                                        {emp.has_login === false ? " · ohne Login" : ""}
                                    </option>
                                ))}
                            </select>
                            <p className="text-[10px] text-blue-700 mt-1">
                                Praktisch wenn der Chef für anwesende No-Login-Mechaniker stempelt.
                                {data?.active_entry && " (Während laufendem Timer nicht änderbar.)"}
                            </p>
                        </div>
                    )}
                    <div className="bg-gray-50 border border-gray-200 rounded-sm p-5 flex flex-col items-center" data-testid="timesheet-control">
                        {data?.active_entry ? (
                            <>
                                <div className="text-[10px] uppercase tracking-wider text-gray-500">Timer läuft seit</div>
                                <div className="text-2xl font-mono-num font-black my-1">{new Date(data.active_entry.started_at).toLocaleTimeString("de-DE")}</div>
                                <Button
                                    onClick={stopTimer}
                                    disabled={busy}
                                    className="bg-red-600 hover:bg-red-700 text-white rounded-sm uppercase tracking-wider px-8 py-6 text-base font-black"
                                    data-testid="timesheet-stop"
                                >
                                    <Square className="w-5 h-5 mr-2" /> Stop
                                </Button>
                            </>
                        ) : (
                            <>
                                <div className="text-[10px] uppercase tracking-wider text-gray-500">Aktueller Status</div>
                                <div className="text-base font-bold mb-2">Kein Timer aktiv</div>
                                <Button
                                    onClick={startTimer}
                                    disabled={busy}
                                    className="bg-green-600 hover:bg-green-700 text-white rounded-sm uppercase tracking-wider px-8 py-6 text-base font-black"
                                    data-testid="timesheet-start"
                                >
                                    <Play className="w-5 h-5 mr-2" /> Start
                                </Button>
                            </>
                        )}
                    </div>

                    {data && (
                        <div className="grid grid-cols-4 gap-2">
                            <div className="bg-white border border-gray-200 rounded-sm p-3 text-center">
                                <div className="text-[10px] uppercase tracking-wider text-gray-500">Minuten erfasst</div>
                                <div className="font-mono-num font-black text-lg" data-testid="timesheet-total-min">{data.total_min}</div>
                            </div>
                            <div className="bg-white border border-gray-200 rounded-sm p-3 text-center">
                                <div className="text-[10px] uppercase tracking-wider text-gray-500">≈ Stunden</div>
                                <div className="font-mono-num font-black text-lg">{data.total_hours}</div>
                            </div>
                            <div className="bg-teal-50 border border-teal-200 rounded-sm p-3 text-center" title="Offene (nicht abgerechnete) Zeit in Arbeitswerten — 1 AW = 6 min, aufgerundet">
                                <div className="text-[10px] uppercase tracking-wider text-teal-700">≈ AW offen</div>
                                <div className="font-mono-num font-black text-lg text-teal-900" data-testid="timesheet-open-aw">{openAw}</div>
                            </div>
                            <div className="bg-white border border-gray-200 rounded-sm p-3 text-center">
                                <div className="text-[10px] uppercase tracking-wider text-gray-500">Einträge</div>
                                <div className="font-mono-num font-black text-lg">{data.items.length}</div>
                            </div>
                        </div>
                    )}

                    {data?.items && data.items.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="timesheet-list">
                            <div className="px-3 py-2 bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500">Erfasste Zeiten</div>
                            <ul className="divide-y divide-gray-100 max-h-60 overflow-y-auto">
                                {data.items.map((e) => (
                                    <li key={e.id} className="px-3 py-2 text-xs flex items-center justify-between" data-testid={`timesheet-entry-${e.id}`}>
                                        <div>
                                            <div className="font-bold">
                                                {new Date(e.started_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}
                                                {e.stopped_at && <span> – {new Date(e.stopped_at).toLocaleTimeString("de-DE")}</span>}
                                                {!e.stopped_at && <span className="ml-2 text-amber-700 font-bold">LÄUFT</span>}
                                            </div>
                                            <div className="text-[10px] text-gray-500">
                                                {e.user_label} · {e.source === "manual" ? "manuell" : "Timer"}
                                                {e.invoiced && <span className="ml-2 text-green-700 font-bold">abgerechnet</span>}
                                            </div>
                                        </div>
                                        <div className="text-right flex items-center gap-3">
                                            <span className="font-mono-num font-bold">{e.duration_min} min</span>
                                            {!e.invoiced && (
                                                <button onClick={() => deleteEntry(e.id)} className="text-red-600 hover:bg-red-50 p-1 rounded-sm" title="Löschen" data-testid={`timesheet-del-${e.id}`}>
                                                    <Trash2 className="w-3 h-3" />
                                                </button>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {data && data.total_min > 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 space-y-2">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-900">Als Lohnposition übernehmen</div>
                            <div className="flex gap-2 items-end">
                                <div className="flex-1">
                                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Stundensatz €/h</label>
                                    <Input
                                        type="number" min="0" step="5"
                                        value={rate}
                                        onChange={(e) => setRate(e.target.value)}
                                        className="rounded-sm mt-1 bg-white"
                                        data-testid="timesheet-rate"
                                    />
                                </div>
                                <Button
                                    onClick={convertToPosition}
                                    disabled={busy}
                                    className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm uppercase tracking-wider font-black"
                                    data-testid="timesheet-to-position"
                                >
                                    <Check className="w-3 h-3 mr-1" /> Übernehmen
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
